<template>
  <div class="casino-sticky-panel" :class="[activeKey]">
    <div class="buttons-container">
      <div class="close" @click="$emit('close')"></div>
    </div>

    <p class="panel-text" v-if="['RemoveAllFavorites'].includes(activeKey)"
       v-html="$t('Casino.Tab.Panel.Text.' + activeKey, { count: itemCount })">
    </p>

    <!-- SortBy Panel -->
    <div v-if="activeKey === 'SortBy'">
      <div v-for="option in options" :key="option.value" class="radio-option">
        <input
            type="radio"
            :id="option.id"
            :value="option.value"
            v-model="selectedSortOrder"
            @change="onRadioChange">
        <label :for="option.id">{{ option.label }}</label>
      </div>
    </div>

    <!-- FilterBy Panel -->
    <div class="filter-section" v-if="activeKey === 'FilterBy'">
      <h3>{{$t('Casino.Tab.Filter.Title')}}</h3>
      <h2>{{$t('Casino.Tab.Filter.Text')}}</h2>
      <div class="content-wrapper">
        <div v-for="dropdown in filterDropdowns" :key="dropdown.id" class="dropdown">
          <button @click="toggleDropdown(dropdown.id)" class="dropdown-btn">
            {{ toCapitalCase(dropdown.label) }}
            <span v-if="selectedFilters[dropdown.id] && selectedFilters[dropdown.id].length">
            ({{ selectedFilters[dropdown.id].length }})
          </span>
            <span class="dropdown-arrows">
            <v-icon v-if="openDropdowns.includes(dropdown.id)">expand_less</v-icon>
            <v-icon v-else>expand_more</v-icon>
          </span>
          </button>

          <div v-if="openDropdowns.includes(dropdown.id)" class="dropdown-content">
            <div v-for="item in dropdown.options" :key="item.id">
              <input
                type="checkbox"
                :id="`${dropdown.id}-${item.id}`"
                :value="item.id"
                v-model="selectedFilters[dropdown.id]"
                @change="countSelectedFilters()">
              <label :for="`${dropdown.id}-${item.id}`">{{ item.name }}</label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="['RemoveAllFavorites', 'FilterBy'].includes(activeKey)" class="casino-tabs-action-buttons">
      <v-btn v-if="['FilterBy'].includes(activeKey)" @click="resetFilters()" class="button button-close">
        {{ $t('Casino.Tab.Panel.Button.Reset') }}
      </v-btn>
      <v-btn v-if="['RemoveAllFavorites'].includes(activeKey)" @click="$emit('close')" class="button button-close">
        {{ $t('Casino.Tab.Panel.Button.Close') }}
      </v-btn>
      <v-btn @click="buttonAction(activeKey)" class="button button-action" v-html="$t('Casino.Tab.Panel.Button.Action.' + activeKey, { count: (filteredItems && filteredItems.length > 0 && filteredItems.length !== originalPreparedData.length ? `(${filteredItems.length})` : `${selectedFilterNumberDisplay > 0 ? `(0)` : ''}`) })">
      </v-btn>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import Casino from '@/components/mixins/Casino'

export default {
  name: 'filterAndSortPanel',
  props: ['activeKey', 'itemCount', 'providers', 'themeList', 'filteredItems', 'originalPreparedData', 'selectedFilterNumberDisplay', 'pageSection'],
  mixins: [Casino],
  data () {
    return {
      methodMap: {
        'RemoveAllFavorites': 'removeAllFavorites'
      },
      selectedSortOrder: 'default',
      options: [
        { id: 'default', value: 'default', label: 'Recommended' },
        { id: 'asc', value: 'asc', label: 'A-Z' },
        { id: 'desc', value: 'desc', label: 'Z-A' }
      ],
      openDropdowns: [],
      filterDropdowns: [],
      selectedFilters: {
        providers: [],
        themes: []
      }
    }
  },

  methods: {
    resetFilters () {
      this.selectedFilters = {
        providers: [],
        themes: []
      }
      this.countSelectedFilters(false, true)
      this.openDropdowns = []
    },

    buttonAction (action) {
      this.$emit('close')

      if (this.methodMap[this.activeKey]) {
        store.dispatch(`gap/${this.methodMap[this.activeKey]}`)
            .then(response => {
              this.$emit('actionCompleted', { action: this.activeKey, response })
            })
            .catch(error => {
              this.$emit('actionError', { action: this.activeKey, error })
            })
        return
      }

      if (action === 'FilterBy') {
        this.countSelectedFilters(true, false)
      }
    },

    onRadioChange () {
      this.$emit('sortTypeSelected', this.selectedSortOrder)
    },

    toggleDropdown (dropdownId) {
      const index = this.openDropdowns.indexOf(dropdownId)
      if (index > -1) {
        this.openDropdowns.splice(index, 1)
      } else {
        this.openDropdowns.push(dropdownId)
      }
    },

    countSelectedFilters (apply = false, reset = false) {
      this.$emit('filterSelected', {
        selected: this.selectedFilters,
        apply: apply,
        reset: reset,
        pageSection: this.pageSection
      })
    },

    updateDropdownOptions (option, data, orderId = 999) {
      if (!data || data.length === 0) return

      const filtered = this.filterDropdowns.filter(item => item.id !== option)
      const newDropdown = {
        id: option,
        label: option,
        options: data,
        orderId: orderId
      }

      this.filterDropdowns = [...filtered, newDropdown].sort((a, b) => a.orderId - b.orderId)

      if (!this.selectedFilters[option]) {
        this.$set(this.selectedFilters, option, [])
      }
    },

    resetAll () {
      this.openDropdowns = []
      this.filterDropdowns = []
      this.selectedFilters = {
        providers: [],
        themes: []
      }
      this.selectedSortOrder = 'default'
    }
  },

  watch: {
    themeList (newVal) {
      if (!newVal || newVal.length === 0) return
      this.updateDropdownOptions('themes', newVal, 1)
    },

    providers (newVal) {
      if (!newVal || newVal.length === 0) return
      this.updateDropdownOptions('providers', newVal, 2)
    },

    '$route' () {
      this.resetFilters()
      this.$emit('close')
    }
  },

  created () {
    this.filterDropdowns.forEach(dropdown => {
      this.$set(this.selectedFilters, dropdown.id, [])
    })
  },

  mounted () {
    if (this.themeList && this.themeList.length > 0) {
      this.updateDropdownOptions('themes', this.themeList, 1)
    }
    if (this.providers && this.providers.length > 0) {
      this.updateDropdownOptions('providers', this.providers, 2)
    }
  },

  beforeDestroy () {
    this.resetAll()
  }
}
</script>

<style scoped lang="stylus">
@import '~@/css/config'
@import '~@/css/mixins'
.casino-sticky-panel
  width: 200px
  padding: 15px
  border-radius: 10px
  border: 1px solid #000
  background: var(--casino-game-info-bgr)
  color: var(--casino-game-info-color)
  display: flex
  flex-direction: column
  opacity: 0.95
  .filter-section
    flex: 1
    overflow-y: auto
    overflow-x: hidden
    min-height: 0
    scrollbar-width: none
    -ms-overflow-style: none
  .panel-text
    text-align: center
    font-size: 14px

  .radio-option
    margin-bottom: 5px
    &:last-child
      margin-bottom: 0

    input[type="radio"]
      appearance: none
      -webkit-appearance: none
      width: 13px
      height: 13px
      border: 2px solid #FAA21C
      border-radius: 5px
      outline: none
      cursor: pointer
      position: relative
      background: transparent
      opacity: 1
      transition: all 0.2s ease

      &:hover
        border-color: #FAA21C

      &:checked
        background: #FAA21C
        border-color: #FAA21C

    label
      cursor: pointer
      margin-left: 8px

  input[type="checkbox"]
    appearance: none
    -webkit-appearance: none
    width: 13px
    height: 13px
    border: 2px solid #FF6B00
    border-radius: 5px
    outline: none
    cursor: pointer
    position: relative
    background: transparent
    opacity: 1
    transition: all 0.2s ease
    margin-right: 8px

    &:hover
      border-color: #FF8C00

    &:checked
      background: #FF6B00
      border-color: #FF6B00

      &::after
        content: '✓'
        position: absolute
        color: #FFFFFF
        font-size: 10px
        font-weight: bold
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
        line-height: 1

  .dropdown-content
    > div
      margin-bottom: 8px
      display: flex
      align-items: center
      &:last-child
        margin-bottom: 0

  label
    cursor: pointer
    user-select: none

  .dropdown
    .dropdown-btn
      font-size: 18px
      font-family: 'Roboto Condensed Bold'
      margin-bottom: 5px

      .dropdown-arrows
        .v-icon
          color: #FFFFFF

    .dropdown-content
      > div
        display: inline-block
        margin-right: 8px
        margin-bottom: 8px

        input[type="checkbox"]
          display: none

        label
          background-color: #212631
          display: inline-block
          padding: 5px
          cursor: pointer
          transition: all 0.2s ease
          color: #CBCFDA
          font-size: 14px
          min-width: 50px
          min-height: 33px
          border-radius: 5px

        input[type="checkbox"]:checked + label
          background: #FAA21C
          color: #212631

  &.RemoveAllFavorites
    width: 250px

  &.FilterBy
    width: 100%
    height: 100%
    border-radius: 0
    h3, h2
      display: flex
      justify-content: center
      align-items: center
      font-size 25px
    h2
      font-size 20px
      justify-content flex-start
    @media mobile-big-and-below
      height 95%

  .buttons-container
    margin-bottom: 5px

    .close
      background-image: var(--casino-dialog-close-icon-grey)
      width: 14px
      display: block
      height: 14px
      margin-left: auto
      cursor: pointer

  .casino-tabs-action-buttons
    display: flex
    margin-top: auto
    margin-bottom: 5px

    .button
      width: 100px
      border-radius: 5px
      margin: 0
      font-family: 'Roboto Condensed Bold'
      font-size: 16px
      flex: 1 auto

    .button-action
      background: var(--casino-play-game-btn-bgr)
      color: var(--casino-play-game-btn-color)
      border: var(--casino-play-game-btn-border)

    .button-close
      background: var(--casino-game-info-bgr)
      color: var(--casino-play-game-btn-color)
      border: var(--casino-play-game-btn-border)
      margin-right: 5px
</style>



// WEBPACK FOOTER //
// src/components/igaming/partials/filterAndSortPanel.vue